import React, { useState, useEffect } from "react";
import { apiClient, useAuth } from "../App";
import { toast } from "sonner";
import { HelpBubble } from "../components/HelpBubble";
import {
  Shield, Plus, Pencil, Trash2, ChevronDown, ChevronUp, ToggleLeft, ToggleRight, Loader2,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";

const CONDITION_FIELDS = [
  { value: "contract_value", label: "Contract Value" },
  { value: "contract_type", label: "Contract Type" },
  { value: "spend_category", label: "Spend Category" },
  { value: "department", label: "Department" },
  { value: "jurisdiction", label: "Jurisdiction" },
  { value: "currency", label: "Currency" },
];

const OPERATORS = {
  contract_value: [
    { value: "gt", label: "> (greater than)" },
    { value: "gte", label: ">= (greater or equal)" },
    { value: "lt", label: "< (less than)" },
    { value: "lte", label: "<= (less or equal)" },
    { value: "eq", label: "= (equals)" },
  ],
  contract_type: [
    { value: "eq", label: "equals" },
    { value: "in", label: "is one of (comma-separated)" },
    { value: "neq", label: "not equal" },
  ],
  spend_category: [
    { value: "eq", label: "equals" },
    { value: "in", label: "is one of (comma-separated)" },
  ],
  department: [
    { value: "eq", label: "equals" },
    { value: "in", label: "is one of (comma-separated)" },
  ],
  jurisdiction: [
    { value: "eq", label: "equals" },
    { value: "in", label: "is one of (comma-separated)" },
  ],
  currency: [
    { value: "eq", label: "equals" },
    { value: "in", label: "is one of (comma-separated)" },
  ],
};

const ROLES = [
  { value: "admin", label: "Admin" },
  { value: "legal", label: "Legal" },
  { value: "finance", label: "Finance" },
  { value: "executive", label: "Executive" },
  { value: "contract_creator", label: "Contract Creator" },
  { value: "contract_owner", label: "Contract Owner" },
];

const emptyForm = {
  name: "",
  description: "",
  conditions: [],
  required_reviewer_roles: [],
  required_approver_roles: [],
  required_approver_users: [],
  is_active: true,
  priority: 0,
};

export default function ApprovalPoliciesPage() {
  const { user, canDo } = useAuth();
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [approverUsers, setApproverUsers] = useState([]);
  const [filterOptions, setFilterOptions] = useState({});

  useEffect(() => { fetchPolicies(); fetchApproverUsers(); fetchFilterOptions(); }, []);

  const fetchPolicies = async () => {
    try {
      const res = await apiClient.get("/approval-policies");
      setPolicies(res.data);
    } catch (e) {
      toast.error("Failed to load policies");
    } finally {
      setLoading(false);
    }
  };

  const fetchApproverUsers = async () => {
    try {
      const res = await apiClient.get("/users/approvers");
      setApproverUsers(res.data);
    } catch { /* silent */ }
  };

  const fetchFilterOptions = async () => {
    try {
      const res = await apiClient.get("/contract-management/filters");
      setFilterOptions(res.data);
    } catch { /* silent */ }
  };

  const openCreate = () => {
    setEditingId(null);
    setForm({ ...emptyForm });
    setDialogOpen(true);
  };

  const openEdit = (policy) => {
    setEditingId(policy.id);
    setForm({
      name: policy.name,
      description: policy.description || "",
      conditions: policy.conditions || [],
      required_reviewer_roles: policy.required_reviewer_roles || [],
      required_approver_roles: policy.required_approver_roles || [],
      required_approver_users: policy.required_approver_users || [],
      is_active: policy.is_active ?? true,
      priority: policy.priority || 0,
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    try {
      if (editingId) {
        await apiClient.put(`/approval-policies/${editingId}`, form);
        toast.success("Policy updated");
      } else {
        await apiClient.post("/approval-policies", form);
        toast.success("Policy created");
      }
      setDialogOpen(false);
      fetchPolicies();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save policy");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this policy?")) return;
    try {
      await apiClient.delete(`/approval-policies/${id}`);
      toast.success("Policy deleted");
      fetchPolicies();
    } catch (e) {
      toast.error("Failed to delete");
    }
  };

  const addCondition = () => {
    setForm({ ...form, conditions: [...form.conditions, { field: "contract_value", operator: "gt", value: "" }] });
  };

  const updateCondition = (idx, key, val) => {
    const c = [...form.conditions];
    c[idx] = { ...c[idx], [key]: val };
    if (key === "field") {
      c[idx].operator = OPERATORS[val]?.[0]?.value || "eq";
      c[idx].value = "";
    }
    setForm({ ...form, conditions: c });
  };

  const removeCondition = (idx) => {
    setForm({ ...form, conditions: form.conditions.filter((_, i) => i !== idx) });
  };

  const toggleRole = (list, role) => {
    const current = form[list];
    setForm({
      ...form,
      [list]: current.includes(role) ? current.filter((r) => r !== role) : [...current, role],
    });
  };

  const isAdmin = canDo("workflows.create_edit");

  if (loading) return <div className="flex items-center justify-center min-h-[400px]"><div className="spinner" /></div>;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="approval-policies-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 dark:text-white" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Approval Policies <HelpBubble tip="Attribute-based rules that auto-assign approvers to contracts. Contracts cannot be approved without a matching policy. Highest priority policy wins." />
          </h1>
          <p className="text-slate-500 mt-1">Configure rules that determine required reviewers and approvers based on contract attributes</p>
        </div>
        {isAdmin && (
          <Button onClick={openCreate} className="gap-2 rounded-sm" data-testid="create-policy-btn">
            <Plus size={16} /> New Policy
          </Button>
        )}
      </div>

      {policies.length === 0 ? (
        <div className="text-center py-20">
          <Shield size={48} className="mx-auto text-slate-300 mb-4" />
          <p className="text-slate-500">No approval policies configured</p>
          {isAdmin && <p className="text-sm text-slate-400 mt-1">Create your first policy to automate approval routing</p>}
        </div>
      ) : (
        <div className="space-y-4">
          {policies.map((policy) => (
            <Card key={policy.id} className={`transition-shadow hover:shadow-md ${!policy.is_active ? "opacity-60" : ""}`} data-testid={`policy-card-${policy.id}`}>
              <CardContent className="pt-5 pb-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <Shield size={18} className="text-cyan-600" />
                      <span className="font-medium text-slate-900 dark:text-white">{policy.name}</span>
                      <Badge className={`rounded-sm text-xs ${policy.is_active ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                        {policy.is_active ? "Active" : "Inactive"}
                      </Badge>
                      <Badge className="rounded-sm text-xs bg-slate-100 text-slate-600">Priority: {policy.priority}</Badge>
                    </div>
                    {policy.description && <p className="text-sm text-slate-500 mb-3">{policy.description}</p>}

                    {/* Conditions */}
                    {policy.conditions?.length > 0 && (
                      <div className="mb-3">
                        <p className="text-xs font-medium text-slate-400 uppercase tracking-wider mb-1">Conditions</p>
                        <div className="flex flex-wrap gap-2">
                          {policy.conditions.map((c, i) => (
                            <Badge key={`${c.field}-${c.operator}-${i}`} variant="outline" className="rounded-sm text-xs font-mono">
                              {c.field} {c.operator} {c.value}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="flex gap-6 flex-wrap">
                      {policy.required_reviewer_roles?.length > 0 && (
                        <div>
                          <p className="text-xs font-medium text-slate-400 uppercase tracking-wider mb-1">Required Reviewers</p>
                          <div className="flex gap-1">{policy.required_reviewer_roles.map((r) => (
                            <Badge key={r} className="rounded-sm text-xs bg-amber-100 text-amber-700">{r}</Badge>
                          ))}</div>
                        </div>
                      )}
                      {policy.required_approver_roles?.length > 0 && (
                        <div>
                          <p className="text-xs font-medium text-slate-400 uppercase tracking-wider mb-1">Approver Roles</p>
                          <div className="flex gap-1">{policy.required_approver_roles.map((r) => (
                            <Badge key={r} className="rounded-sm text-xs bg-blue-100 text-blue-700">{r}</Badge>
                          ))}</div>
                        </div>
                      )}
                      {policy.required_approver_users?.length > 0 && (
                        <div>
                          <p className="text-xs font-medium text-slate-400 uppercase tracking-wider mb-1">Specific Approvers</p>
                          <div className="flex gap-1 flex-wrap">{policy.required_approver_users.map((uid) => {
                            const u = approverUsers.find(a => a.id === uid);
                            return (
                              <Badge key={uid} className="rounded-sm text-xs bg-indigo-100 text-indigo-700">
                                {u ? u.name : uid.slice(0, 8)}
                              </Badge>
                            );
                          })}</div>
                        </div>
                      )}
                    </div>
                  </div>

                  {isAdmin && (
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" onClick={() => openEdit(policy)} data-testid={`edit-policy-${policy.id}`}>
                        <Pencil size={14} />
                      </Button>
                      <Button variant="ghost" size="sm" className="text-red-500" onClick={() => handleDelete(policy.id)} data-testid={`delete-policy-${policy.id}`}>
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingId ? "Edit Policy" : "Create Approval Policy"}</DialogTitle>
            <DialogDescription>Define conditions and required roles for contract approvals</DialogDescription>
          </DialogHeader>
          <div className="space-y-5 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Policy Name *</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-sm" data-testid="policy-name-input" />
              </div>
              <div className="space-y-2">
                <Label>Priority</Label>
                <Input type="number" value={form.priority} onChange={(e) => setForm({ ...form, priority: parseInt(e.target.value) || 0 })} className="rounded-sm" data-testid="policy-priority-input" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} className="rounded-sm" />
            </div>

            {/* Conditions */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label>Conditions (all must match)</Label>
                <Button variant="outline" size="sm" onClick={addCondition} className="gap-1 rounded-sm" data-testid="add-condition-btn">
                  <Plus size={14} /> Add Condition
                </Button>
              </div>
              {form.conditions.map((cond, idx) => {
                const isNumeric = cond.field === "contract_value";
                const isCommaList = cond.operator === "in";
                const optionsMap = {
                  contract_type: filterOptions.contract_types || [],
                  department: filterOptions.departments || [],
                  spend_category: filterOptions.spend_categories || [],
                  jurisdiction: filterOptions.jurisdictions || [],
                  currency: ["USD", "GBP", "EUR", "AED", "INR", "SGD", "AUD", "CAD", "CHF", "JPY", "CNY", "ZAR"],
                };
                const suggestions = optionsMap[cond.field] || [];

                return (
                <div key={idx} className="flex items-center gap-2 p-2 border rounded-sm bg-slate-50 dark:bg-slate-800" data-testid={`condition-row-${idx}`}>
                  <Select value={cond.field} onValueChange={(v) => updateCondition(idx, "field", v)}>
                    <SelectTrigger className="w-[160px] rounded-sm"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {CONDITION_FIELDS.map((f) => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Select value={cond.operator} onValueChange={(v) => updateCondition(idx, "operator", v)}>
                    <SelectTrigger className="w-[180px] rounded-sm"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {(OPERATORS[cond.field] || []).map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  {isNumeric || isCommaList || suggestions.length === 0 ? (
                    <Input
                      value={cond.value}
                      onChange={(e) => updateCondition(idx, "value", e.target.value)}
                      placeholder={isNumeric ? "e.g. 50000" : isCommaList ? "Value1, Value2, Value3" : "Value"}
                      className="flex-1 rounded-sm"
                      data-testid={`condition-value-${idx}`}
                    />
                  ) : (
                    <Select value={cond.value} onValueChange={(v) => updateCondition(idx, "value", v)}>
                      <SelectTrigger className="flex-1 rounded-sm" data-testid={`condition-value-${idx}`}>
                        <SelectValue placeholder="Select value" />
                      </SelectTrigger>
                      <SelectContent>
                        {suggestions.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  )}
                  <Button variant="ghost" size="sm" className="text-red-500 h-8 w-8 p-0" onClick={() => removeCondition(idx)}>
                    <Trash2 size={14} />
                  </Button>
                </div>
                );
              })}
            </div>

            {/* Reviewer Roles */}
            <div className="space-y-2">
              <Label>Required Reviewer Roles</Label>
              <p className="text-xs text-slate-400">Users with these roles will be suggested as reviewers</p>
              <div className="flex flex-wrap gap-2">
                {ROLES.map((r) => (
                  <button key={r.value} type="button" onClick={() => toggleRole("required_reviewer_roles", r.value)}
                    className={`px-3 py-1.5 rounded-sm border text-sm transition-colors ${form.required_reviewer_roles.includes(r.value) ? "bg-amber-100 border-amber-300 text-amber-800" : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"}`}
                    data-testid={`reviewer-role-${r.value}`}>
                    {r.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Approver Roles */}
            <div className="space-y-2">
              <Label>Required Approver Roles</Label>
              <p className="text-xs text-slate-400">Users with these roles will be suggested as approvers</p>
              <div className="flex flex-wrap gap-2">
                {ROLES.map((r) => (
                  <button key={r.value} type="button" onClick={() => toggleRole("required_approver_roles", r.value)}
                    className={`px-3 py-1.5 rounded-sm border text-sm transition-colors ${form.required_approver_roles.includes(r.value) ? "bg-blue-100 border-blue-300 text-blue-800" : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"}`}
                    data-testid={`approver-role-${r.value}`}>
                    {r.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Specific Approver Users */}
            <div className="space-y-2">
              <Label>Specific Approvers (Executives)</Label>
              <p className="text-xs text-slate-400">Select individual users who must approve contracts matching this policy</p>
              <div className="space-y-1.5 max-h-48 overflow-y-auto border rounded-sm p-2 bg-slate-50 dark:bg-slate-800">
                {approverUsers.length === 0 ? (
                  <p className="text-xs text-slate-400 text-center py-2">No approver-eligible users found</p>
                ) : (
                  approverUsers.map((u) => {
                    const selected = (form.required_approver_users || []).includes(u.id);
                    return (
                      <label key={u.id} className={`flex items-center gap-3 px-3 py-2 rounded-sm cursor-pointer transition-colors ${selected ? "bg-indigo-50 dark:bg-indigo-950/30 border border-indigo-200 dark:border-indigo-800" : "hover:bg-white dark:hover:bg-slate-700 border border-transparent"}`} data-testid={`approver-user-${u.id}`}>
                        <input
                          type="checkbox"
                          checked={selected}
                          onChange={() => {
                            const current = form.required_approver_users || [];
                            setForm({
                              ...form,
                              required_approver_users: selected ? current.filter(id => id !== u.id) : [...current, u.id],
                            });
                          }}
                          className="rounded"
                        />
                        <div className="flex-1 min-w-0">
                          <span className="text-sm font-medium text-slate-700 dark:text-slate-200">{u.name}</span>
                          <span className="text-xs text-slate-400 ml-2">{u.email}</span>
                        </div>
                        <Badge className="rounded-sm text-[10px] bg-slate-100 text-slate-600 capitalize">{u.role?.replace(/_/g, " ")}</Badge>
                      </label>
                    );
                  })
                )}
              </div>
              {(form.required_approver_users || []).length > 0 && (
                <p className="text-xs text-indigo-600">{form.required_approver_users.length} approver(s) selected — these users will be required to approve matching contracts</p>
              )}
            </div>

            {/* Active Toggle */}
            <div className="flex items-center gap-3">
              <button type="button" onClick={() => setForm({ ...form, is_active: !form.is_active })} className="flex items-center gap-2 text-sm" data-testid="policy-active-toggle">
                {form.is_active ? <ToggleRight size={24} className="text-emerald-600" /> : <ToggleLeft size={24} className="text-slate-400" />}
                {form.is_active ? "Active" : "Inactive"}
              </button>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={handleSave} className="rounded-sm" data-testid="save-policy-btn">
              {editingId ? "Update Policy" : "Create Policy"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
